import { ArrowDownward, ArrowUpward, CalendarMonth, Check, InfoOutlined } from "@mui/icons-material";
import { Box, Button, Divider, FormControl, InputLabel, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { modalAdmissionEmployee, modalEmployeePending, permissionsAtom, userTypeAtom } from "../../atoms/atom";
import { getLoading } from "../../pages/execution-events/ExecutionEvents";
import { EmployeeService } from "../../services/EmployeeService";
import { AccountDTO } from "../../types/AccountDTO";
import { CreatedEmployeeDTO } from "../../types/CreatedEmployeeDTO";
import { useUtils } from "../../utils/utilFunctions";
import EmployeeEditModal from "./EmployeeEditModal";
import AdmissionModal from "./admission/AdmissionModal";

interface Props {
	update: number;
	account: AccountDTO;
}

const EmployeeList: FC<Props> = (props: Props) => {
	const [employees, setEmployees] = useState<CreatedEmployeeDTO[]>([]);
	const [filtered, setFiltered] = useState<CreatedEmployeeDTO[]>([]);
	const [employeeEdit, setEmployeeEdit] = useState<CreatedEmployeeDTO>({} as CreatedEmployeeDTO);
	const [loadingEmployees, setLoading] = useState(false);
	const [permissions] = useAtom(permissionsAtom);
	const [userType] = useAtom(userTypeAtom);
	const [openModal, setOpenModal] = useAtom(modalEmployeePending);
	const [openModalAdmission, setOpenAdmission] = useAtom(modalAdmissionEmployee);
	const [search, setSearch] = useState('');
	const [companyFilter, setCompanyFilter] = useState('ALL');
	const [scheduleFilter, setScheduleFilter] = useState('ALL');
	const [fieldOrder, setFieldOrder] = useState<'COMPANY' | 'NAME' | 'CPF' | 'ID' | 'DATE'>('ID');
	const [ordemAsc, setOrdemAsc] = useState(false);
	const { parseDate, verifyPermission } = useUtils();
	const service = new EmployeeService();
	const externalUser = "EXTERNO" === userType;
	const companyNames = [...new Set(employees.map(e => e.companyName))];

	const handleClickPending = (employee: CreatedEmployeeDTO) => {
		if (externalUser) return;
		setOpenModal(true);
		setEmployeeEdit(employee);
	}

	const handleClickAdmission = (employee: CreatedEmployeeDTO) => {
		if (employee.internallyScheduled) {
			return;
		}
		setEmployeeEdit(employee);
		setOpenAdmission(true);
	}

	function mudaOrdem(field: 'NAME' | 'COMPANY' | 'CPF' | 'DATE') {
		setFieldOrder(field);
		setOrdemAsc(!ordemAsc);
	}

	useEffect(() => {
		const loadEmployees = async () => {
			setLoading(true);
			const response = await service.getCreatedEmployees();
			if (response && response.data) {
				setEmployees(response.data);
				setFiltered(response.data);
			}
			setLoading(false);
		};
		if (!openModal && !openModalAdmission) {
			loadEmployees();
		}
		if (!!permissions.length) {
			verifyPermission(permissions, 'EMPLOYEES');
		}
	}, [props.update, openModal, openModalAdmission]);

	useEffect(() => {
		let results = employees;

		if (companyFilter !== 'ALL') {
			results = results.filter(e => e.companyName === companyFilter);
		}

		if (permissions.includes('SCHEDULING') && scheduleFilter !== 'ALL') {
			results = results.filter(employee => {
				const isSchedulable = employee.scheduled !== null && !employee.pending && employee.schedulable;
				if (!isSchedulable && scheduleFilter !== 'ALL') {
					return false;
				}
				switch (scheduleFilter) {
					case 'NOT_SCHEDULED':
						return !employee.scheduled;
					case 'SCHEDULED':
						return employee.scheduled && !employee.internallyScheduled;
					case 'INTERNALLY_SCHEDULED':
						return employee.internallyScheduled;
					default:
						return true;
				}
			});
		}
		if (search) {
			results = results.filter(item =>
				item.name.toUpperCase().includes(search.toUpperCase()) || item.cpf.toUpperCase().includes(search.toUpperCase())
			);
		}

		setFiltered(results);
	}, [search, employees, companyFilter, scheduleFilter, permissions]);

	return (
		<div className="list-side">
			<div className="form-title">
				<Typography variant="h5">
					Funcionários cadastrados
				</Typography>
			</div>
			<Box id='employee-filter'>
				<FormControl size="small" fullWidth>
					<InputLabel id="company-filter-label">Filtrar empresas</InputLabel>
					<Select labelId="company-filter-label" id="company-filter" value={companyFilter} label="Filtrar empresas" onChange={(e) => setCompanyFilter(e.target.value)} >
						<MenuItem value="ALL"><em>Todas</em></MenuItem>
						{companyNames.map(name => (
							<MenuItem key={name} value={name}>{name}</MenuItem>
						))}
					</Select>
				</FormControl>
				<TextField fullWidth label="Buscar funcionário pelo nome ou CPF" id="employee-search" size="small" value={search} onChange={(e) => setSearch(e.target.value)} />
				{permissions.includes('SCHEDULING') && (
					<FormControl size="small" fullWidth>
						<InputLabel id="schedule-filter-label">Filtrar agendamentos</InputLabel>
						<Select labelId="schedule-filter-label" id="schedule-filter" value={scheduleFilter} label="Filtrar agendamentos" onChange={(e) => setScheduleFilter(e.target.value)} >
							<MenuItem value="ALL"><em>Todos</em></MenuItem>
							<MenuItem value="NOT_SCHEDULED">Não agendados</MenuItem>
							<MenuItem value="SCHEDULED">Agendados</MenuItem>
							<MenuItem value="INTERNALLY_SCHEDULED">Agendados internamente</MenuItem>
						</Select>
					</FormControl>
				)}
			</Box>
			{loadingEmployees
				? getLoading()
				: <>
					{!!filtered.length
						? <Box className="list-card">
							<div className="employee-list-header">
								<p onClick={() => mudaOrdem('COMPANY')}>Empresa {obtemIconeOdem('COMPANY')}</p>
								<p onClick={() => mudaOrdem('NAME')}>Nome {obtemIconeOdem('NAME')}</p>
								<p onClick={() => mudaOrdem('CPF')} className="minor-cpf">CPF {obtemIconeOdem('CPF')}</p>
								<p onClick={() => mudaOrdem('DATE')} >Cadastrado em {obtemIconeOdem('DATE')}</p>
								<p className="minor">Status</p>
							</div>
							<Divider variant="fullWidth" flexItem sx={{borderColor: "#ffffff80"}}/>
							<div className="employee-list">
								{filtered.sort((a, b) => {
									let valueA: string | number;
									let valueB: string | number;
									switch (fieldOrder) {
										case 'COMPANY':
											valueA = a.companyName;
											valueB = b.companyName;
											break;
										case 'CPF':
											valueA = a.cpf;
											valueB = b.cpf;
											break;
										case 'NAME':
											valueA = a.name;
											valueB = b.name;
											break;
										case 'DATE':
											valueA = parseDate(a.createdAt).getTime();
											valueB = parseDate(b.createdAt).getTime();
											break;
										default:
											valueA = a.id;
											valueB = b.id;
											break;
									}
									if (typeof valueA === "string" && typeof valueB === "string") {
										return ordemAsc
											? valueA.localeCompare(valueB)
											: valueB.localeCompare(valueA);
									} else {
										return ordemAsc
											? (valueA as number) - (valueB as number)
											: (valueB as number) - (valueA as number);
									}
								}).map((employee) => (
									<div className="employee-list-item">
										<p>{employee.companyName}</p>
										<p>{employee.name}</p>
										<p className="minor-cpf">{employee.cpf}</p>
										<p>{employee.createdAt}</p>
										<Box className="action-butons-employee minor">
											<Tooltip
												title={employee.pending ? "Precisa ser aprovado por um usuário interno" : "Cadastrado"}
												placement="left-start"
											>
												<Button
													className="minor"
													onClick={() => handleClickPending(employee)}
													sx={{display: "flex", justifyContent: "center"}}
												>
													{employee.pending
														? <InfoOutlined color="warning"/>
														: <Check color="success"/>
													}
												</Button>
											</Tooltip>
											{showScheduling(employee)
												? <Tooltip
													title={getTitleScheduleButton(employee)}
													placement="left-start"
												>
													<Button
														className="minor"
														onClick={() => handleClickAdmission(employee)}
														sx={{display: "flex", justifyContent: "center"}}
													>
														<CalendarMonth color={employee.scheduled ? "success" : "warning"}/>
													</Button>
												</Tooltip>
												: <></>
											}
										</Box>
									</div>
								))}
							</div>
						</Box>
						: <></>
					}
					{!filtered.length
						? <div className="form-title">
							<Typography variant="h5" sx={{marginTop: '30vh'}}>Nenhum resultado</Typography>
						</div>
						: <></>
					}
				</>
			}
			<EmployeeEditModal props={employeeEdit}/>
			<AdmissionModal props={employeeEdit}/>
		</div>
	);

	function showScheduling(employee: CreatedEmployeeDTO) {
		return permissions.includes('SCHEDULING')
			&& employee.scheduled !== null
			&& !employee.pending
			&& employee.schedulable;
	}

	function getTitleScheduleButton(employee: CreatedEmployeeDTO) {
		const internally = employee.internallyScheduled ? " internamente" : "";
		return employee.scheduled
			? `Agendado${internally} por ` + employee.internallyScheduledInfo
			: "Não agendado";
	}

	function obtemIconeOdem(field: string) {
		return field === fieldOrder
			? ordemAsc
				? <ArrowUpward sx={{marginLeft: "5px"}}/> : <ArrowDownward sx={{marginLeft: "5px"}}/>
			: <ArrowDownward sx={{marginLeft: "5px"}}/>;
	}
}

export default EmployeeList;